// react and mui stuff
import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { UserContext } from "../utils/contexts"
import { ToastContext } from "../components/toast"

import { useColorScheme, useTheme } from "@mui/joy/styles"
import AspectRatio from "@mui/joy/AspectRatio"
import Avatar from "@mui/joy/Avatar"
import Button from "@mui/joy/Button"
import Card from "@mui/joy/Card"
import CardContent from "@mui/joy/CardContent"
import CardOverflow from "@mui/joy/CardOverflow"
import Divider from "@mui/joy/Divider"
import Skeleton from "@mui/joy/Skeleton"
import Stack from "@mui/joy/Stack"
import Typography from "@mui/joy/Typography"
import IconButton from "@mui/joy/IconButton"

import Refresh from "@mui/icons-material/Refresh"

// custom components
import ExclusiveButtons, { RadioButton } from "../components/exclusivebuttons"

// data and stuff
import { CliUser, RankedUser } from "../types/users"
import users, { xpEnum } from "../data/placeholderleaderboard"
import { setData, failure, unauth } from "../utils/setdata"
import { updateUser } from "../utils/utils"
import { leaderboardAPI } from "../utils/backend"


const userColor = (theme?: string) => theme === "dark" ? "#4B7B7C" : "lightblue"

// get the top 3 placing colors for the current theme
function getColor(theme: string | undefined, rank: number, id: string, mainUserId: string) {
  const colors = {
    light: ["#c6c6c6", "#ffe452", "#a3bfb5", "sandybrown"],
    dark: ["#383838", "#906E18", "#4d6964", "saddlebrown"],
  }
  if (rank > 3 && id !== mainUserId) rank = 0;
  return colors[theme === "dark" ? "dark" : "light"][rank] || (id === mainUserId ? userColor(theme) : "")
}

type props = {
  user: RankedUser
  life: Boolean
  mainUserId: string
  loading: boolean
}
function Ranking({user, life, mainUserId, loading}: props) {
  const scheme = useColorScheme().mode;
  const theme = useTheme();
  const nav = useNavigate();

  const color = getColor(scheme, user?.rank ?? 0, user?.id ?? "", mainUserId);
  const style: React.CSSProperties = { display: "flex", margin: "5px", paddingRight: "30px", alignItems: "center", borderRadius: "10px", border: `1px solid ${theme.palette.neutral.outlinedBorder}`, background: color, textAlign: "left", cursor: "pointer", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };

  

  return (
    <button style={style} className="usercard"
      aria-label={`Rank ${user.rank}: ${user.displayName}. Level ${user.level}, XP: ${life ? user.xp.lifetime : user.xp.season}`}
      onClick={() => nav(`../profile/${user.id}`, {relative: "route"})}
      disabled={loading}
    >
      <Typography level="h1" sx={{ marginRight: "15px", marginLeft: "10px", width: "40px", textAlign: "center" }} aria-hidden>
        <Skeleton loading={loading} aria-hidden>
          {user.rank}
        </Skeleton>
      </Typography>
      <Avatar size="lg" src={loading ? "" : user.avatar + "?size=48"} sx={{marginRight: "10px"}} aria-hidden>
        <Skeleton aria-hidden loading={loading}/>
      </Avatar>
      <Stack aria-hidden sx={{minWidth: 0, whiteSpace: "nowrap"}}>
        <Typography level="h3" sx={{minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}} aria-hidden>
          <Skeleton loading={loading} aria-hidden>
            {user.displayName}
          </Skeleton>
        </Typography>
        <Divider aria-hidden/>
        <Typography aria-hidden textColor={"text.primary"}>
          <Skeleton aria-hidden loading={loading}>
            Level {user.level} | {life ? user.xp.lifetime : user.xp.season}xp
          </Skeleton>
        </Typography>
      </Stack>
    </button>
  )
}


export default function Leaderboard() {
  const { user, setUser } = useContext(UserContext)

  const placeholder = {
    season: users("Placeholder"),
    life: users("Placeholder")
  };
  const [board, setBoard] = useState(placeholder);
  const [life, setLife] = useState<string>("season");
  const [loading, setLoading] = useState(true);
  const [fetched, setFetched] = useState(false);
  const [reloadAvailable, setReloadAvailable] = useState(false);
  const [newSetting, setNewSetting] = useState(xpEnum[(user?.xpSetting ?? 2)]);

  const { setToast } = useContext(ToastContext);
  const scheme = useColorScheme().mode;
  
  function getLeaderboard(authCheck: boolean, usr: CliUser | null | undefined, setT: any, setUsr: any) {
    return leaderboardAPI.getLeaderboard(!Boolean(usr?.xp)).then(board => {
      if (!usr) return;

      if (authCheck) {
        if (!setData(board, setT, usr, setUsr)) return;
      } else {
        if (unauth(board, usr, setT)) return;
        if (failure(board, setT)) return;
      }

      // set the data
      
      setBoard(board)
      if (board.user) {
        setUsr(updateUser(usr, board.user))
        setNewSetting(xpEnum[usr.xpSetting ?? 2])
      }
      return true;
    })
  }

  useEffect(() => {
    if (!user || fetched) return;
    getLeaderboard(true, user, setToast, setUser).then(() => {
      setFetched(true)
      setLoading(false)
    })
    // get the info
  }, [setToast, setUser, user, fetched])

  useEffect(() => {
    if (!fetched || reloadAvailable) return;
    const timer = setTimeout(() => {
      setReloadAvailable(true);
    }, 5 * 60_000);

    return () => clearInterval(timer)
  }, [fetched, reloadAvailable, setToast, setUser, user])


  if (!user) return <div/>

  // get the 2 users around the client
  function getAround(): RankedUser[] {
    // get the starting user position (if cliuser has opted in)
    if (!user || user.xpSetting === 0) return []
    let index = Math.max(board[life as keyof typeof board].map(b => b.id).indexOf(user.id) - 1, 0)
    
    // change if they're last so as to not break it
    if (index === board[life as keyof typeof board].length - 2) index--

    // get the neighboring users
    const output: RankedUser[] = [];
    for (let i = index; i < index + 3; i++) {
      const element = board[life as keyof typeof board][i];
      output.push(element);
    }

    return output;
  }

  const around = getAround();

  function setXPSetting() {
    // secondary loading
    setLoading(true);
    if (!user) return;

    // get the data
    leaderboardAPI.enableXP(newSetting).then(info => {
      if (!setData(info, setToast, user, setUser, () => setLoading(false))) return;
      
      // set data and confirm visually
      setBoard(info.leaderboard);
      setToast({ text: "Your XP setting has been updated!", color: "success" })
      setUser(updateUser(user, info.user));
      setLoading(false);
    })
  }

  return (
    <div className="contentRoot">
      <style>{`.usercard:hover {
        filter: brightness(1.1)
      }`}
      </style>

      {/** Leaderboard Selector */}
      <h1 className="pageTitle">Community Leaderboard</h1>
      <ExclusiveButtons value={life} setValue={setLife} label="Rankings:">
        <RadioButton selected={life} value="season" label="Season"/>
        <RadioButton selected={life} value="life" label="Lifetime"/>
        <IconButton disabled={!reloadAvailable || loading} sx={{ paddingLeft: "10px" }} onClick={() => {
          setLoading(true)
          getLeaderboard(true, user, setToast, setUser).then(() => {
            setLoading(false);
            setReloadAvailable(false);
          })
        }}>
          <Refresh/>
        </IconButton>
      </ExclusiveButtons>
      <br/>
      <Stack direction={{ md: "column", lg: "row" }} sx={{ width: "100%" }} spacing={2}>
        {/** Personal Ranking Card */}
        <Card sx={{ '--icon-size': '100px', width: "100%" }} variant="soft">

          {/** Personal Ranking Header */}
          <CardOverflow variant="solid" sx={{ bgcolor: userColor(scheme) }}>
            <AspectRatio variant="outlined" ratio="1" sx={{ m: 'auto', transform: 'translateY(50%)', borderRadius: '50%', width: 'var(--icon-size)', boxShadow: 'sm', bgcolor: 'background.surface', position: 'relative' }}>
              <div>
                <Avatar src={user.avatar} sx={{ width: "98px", height: "98px" }} alt={`${user.displayName}'s profile picture`}/>
              </div>
            </AspectRatio>
          </CardOverflow>

          {/** Personal Ranking */}
          <Typography level="h3" sx={{ mt: 'calc(var(--icon-size) / 2)', textAlign: "center" }}>My Ranking</Typography>
          <CardContent>
            <Typography>Level: {user.level}</Typography>
            <Typography>XP: {(life === "life" ? user.xp?.lifetime : user.xp?.season) ?? 0}</Typography>
            <Typography>XP to Level {(user.level ?? 0) + 1}: {user.toNextLevel ?? "Unknown"}</Typography>
            {(user.xpSetting || 0) > 0 && around.map(a => <Ranking user={a} life={life === "life"} key={a.id} mainUserId={user.id} loading={loading}/>)}
            {user.xpSetting === 0 &&
                <Typography level="title-md">You're not tracking XP! To start, select a new setting, or use <Typography color="primary" variant="outlined">/rank track</Typography> in Discord.</Typography>
            }

            {/** XP Setting */}
            <ExclusiveButtons value={newSetting} setValue={setNewSetting} label="XP Setting">
              <RadioButton selected={newSetting} value={"FULL"} label="Full"/>
              <RadioButton selected={newSetting} value={"SILENT"} label="Silent"/>
              <RadioButton selected={newSetting} value={"OFF"} label="Off"/>
              <Button loading={loading} disabled={newSetting === xpEnum[user.xpSetting ?? 0]} onClick={setXPSetting}>Save</Button>
            </ExclusiveButtons>
          </CardContent>
        </Card>

        {/** Leaderboard */}
        <br/>
        <Card variant="soft" sx={{ height: "573px", overflowY: "auto", width: "100%" }}>
          <CardOverflow/>
          <CardContent>
            {life === "life" && board.life.map(usr => <Ranking user={usr} life={true} key={usr.id} mainUserId={user.id} loading={loading}/>)}
            {life !== "life" && board.season.map(usr => <Ranking user={usr} life={false} key={usr.id} mainUserId={user.id} loading={loading}/>)}
          </CardContent>
        </Card>
      </Stack>
      <br/>
    </div>
  )
}